/**
 * Web API - Electron API Replacement
 * ===================================
 *
 * Provides a compatible API surface to replace window.electronAPI
 * for web-based deployments. Uses HTTP/WebSocket instead of IPC.
 *
 * This allows the same React components to work in both Electron
 * and web browser environments.
 */

import {
  terminalApi,
  filesApi,
  settingsApi,
  projectsApi,
  healthApi,
  type TerminalSession,
  type Settings,
  type ProjectInfo,
  type FileEntry,
} from './api-client';
import {
  TerminalWebSocket,
  terminalWsManager,
  type TerminalWebSocketCallbacks,
} from './terminal-websocket';

// =============================================================================
// Type Definitions (matching Electron API types)
// =============================================================================

export interface IPCResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface TerminalCreateOptions {
  cwd?: string;
  cols?: number;
  rows?: number;
  shell?: string;
  claudeProfile?: string;
  worktreeConfig?: unknown;
}

// =============================================================================
// Terminal State Management
// =============================================================================

interface TerminalState {
  id: string;
  ws: TerminalWebSocket | null;
  callbacks: {
    output: Set<(id: string, data: string) => void>;
    exit: Set<(id: string, exitCode: number) => void>;
    titleChange: Set<(id: string, title: string) => void>;
    claudeSession: Set<(id: string, sessionId: string) => void>;
    claudeBusy: Set<(id: string, isBusy: boolean) => void>;
  };
}

const terminals = new Map<string, TerminalState>();

// Global callback registrations
const globalCallbacks = {
  output: new Set<(id: string, data: string) => void>(),
  exit: new Set<(id: string, exitCode: number) => void>(),
  titleChange: new Set<(id: string, title: string) => void>(),
  claudeSession: new Set<(id: string, sessionId: string) => void>(),
  claudeBusy: new Set<(id: string, isBusy: boolean) => void>(),
  claudeExit: new Set<(id: string) => void>(),
  rateLimit: new Set<(info: unknown) => void>(),
};

// =============================================================================
// Web API Implementation
// =============================================================================

/**
 * Web-based API that replaces window.electronAPI
 */
export const webAPI = {
  // ===========================================================================
  // Terminal Operations
  // ===========================================================================

  createTerminal: async (options: TerminalCreateOptions): Promise<IPCResult<{ id: string }>> => {
    const result = await terminalApi.create({
      cwd: options.cwd,
      cols: options.cols,
      rows: options.rows,
      shell: options.shell,
    });

    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }

    const session = result.data;
    const terminalId = session.id;

    // Create terminal state
    const state: TerminalState = {
      id: terminalId,
      ws: null,
      callbacks: {
        output: new Set(),
        exit: new Set(),
        titleChange: new Set(),
        claudeSession: new Set(),
        claudeBusy: new Set(),
      },
    };
    terminals.set(terminalId, state);

    // Connect WebSocket
    try {
      const wsCallbacks: TerminalWebSocketCallbacks = {
        onOutput: (data: Uint8Array) => {
          // Convert to string for compatibility with existing handlers
          const text = new TextDecoder().decode(data);
          // Notify all listeners
          globalCallbacks.output.forEach((cb) => cb(terminalId, text));
          state.callbacks.output.forEach((cb) => cb(terminalId, text));
        },
        onClose: (code: number, reason: string) => {
          console.log(`Terminal ${terminalId} WebSocket closed: ${code} ${reason}`);
          globalCallbacks.exit.forEach((cb) => cb(terminalId, code === 1000 ? 0 : 1));
          state.callbacks.exit.forEach((cb) => cb(terminalId, code === 1000 ? 0 : 1));
        },
        onError: (error: string) => {
          console.error(`Terminal ${terminalId} WebSocket error: ${error}`);
        },
      };

      state.ws = await terminalWsManager.create(terminalId, wsCallbacks);
    } catch (error) {
      console.error('Failed to connect terminal WebSocket:', error);
      // Still return success - terminal exists, just no WebSocket
    }

    return { success: true, data: { id: terminalId } };
  },

  destroyTerminal: async (id: string): Promise<IPCResult> => {
    // Close WebSocket
    terminalWsManager.close(id);
    terminals.delete(id);

    // Destroy on server
    const result = await terminalApi.destroy(id);
    return { success: result.success, error: result.error };
  },

  sendTerminalInput: (id: string, data: string): void => {
    const ws = terminalWsManager.get(id);
    if (ws) {
      ws.sendInput(data);
    }
  },

  resizeTerminal: (id: string, cols: number, rows: number): void => {
    const ws = terminalWsManager.get(id);
    if (ws) {
      ws.resize(cols, rows);
    }
    // Also update on server
    terminalApi.resize(id, cols, rows).catch(console.error);
  },

  invokeClaudeInTerminal: (id: string, _cwd?: string): void => {
    terminalApi.invokeClaude(id).catch(console.error);
  },

  generateTerminalName: async (
    _command: string,
    _cwd?: string
  ): Promise<IPCResult<string>> => {
    // For web version, just generate a timestamp-based name
    const name = `Terminal ${new Date().toLocaleTimeString()}`;
    return { success: true, data: name };
  },

  setTerminalTitle: (id: string, title: string): void => {
    terminalApi.setTitle(id, title).catch(console.error);
    // Notify listeners
    globalCallbacks.titleChange.forEach((cb) => cb(id, title));
    terminals.get(id)?.callbacks.titleChange.forEach((cb) => cb(id, title));
  },

  setTerminalWorktreeConfig: (_id: string, _config: unknown): void => {
    // Not implemented for web version
  },

  // Terminal Session Management (simplified for web)
  getTerminalSessions: async (_projectPath: string): Promise<IPCResult<unknown[]>> => {
    return { success: true, data: [] };
  },

  restoreTerminalSession: async (
    _session: unknown,
    _cols?: number,
    _rows?: number
  ): Promise<IPCResult<unknown>> => {
    return { success: false, error: 'Session restore not supported in web version' };
  },

  clearTerminalSessions: async (_projectPath: string): Promise<IPCResult> => {
    return { success: true };
  },

  resumeClaudeInTerminal: (id: string, sessionId?: string): void => {
    terminalApi.invokeClaude(id, sessionId).catch(console.error);
  },

  activateDeferredClaudeResume: (_id: string): void => {
    // Not implemented for web version
  },

  checkTerminalPtyAlive: async (terminalId: string): Promise<IPCResult<{ alive: boolean }>> => {
    const ws = terminalWsManager.get(terminalId);
    return { success: true, data: { alive: ws?.isConnected() ?? false } };
  },

  // ===========================================================================
  // Terminal Event Listeners
  // ===========================================================================

  onTerminalOutput: (callback: (id: string, data: string) => void): (() => void) => {
    globalCallbacks.output.add(callback);
    return () => globalCallbacks.output.delete(callback);
  },

  onTerminalExit: (callback: (id: string, exitCode: number) => void): (() => void) => {
    globalCallbacks.exit.add(callback);
    return () => globalCallbacks.exit.delete(callback);
  },

  onTerminalTitleChange: (callback: (id: string, title: string) => void): (() => void) => {
    globalCallbacks.titleChange.add(callback);
    return () => globalCallbacks.titleChange.delete(callback);
  },

  onTerminalWorktreeConfigChange: (
    _callback: (id: string, config: unknown) => void
  ): (() => void) => {
    return () => {}; // No-op for web
  },

  onTerminalClaudeSession: (callback: (id: string, sessionId: string) => void): (() => void) => {
    globalCallbacks.claudeSession.add(callback);
    return () => globalCallbacks.claudeSession.delete(callback);
  },

  onTerminalRateLimit: (callback: (info: unknown) => void): (() => void) => {
    globalCallbacks.rateLimit.add(callback);
    return () => globalCallbacks.rateLimit.delete(callback);
  },

  onTerminalClaudeBusy: (callback: (id: string, isBusy: boolean) => void): (() => void) => {
    globalCallbacks.claudeBusy.add(callback);
    return () => globalCallbacks.claudeBusy.delete(callback);
  },

  onTerminalClaudeExit: (callback: (id: string) => void): (() => void) => {
    globalCallbacks.claudeExit.add(callback);
    return () => globalCallbacks.claudeExit.delete(callback);
  },

  // Stub implementations for OAuth/Profile events (not used in web)
  onTerminalOAuthToken: (_callback: unknown): (() => void) => () => {},
  onTerminalAuthCreated: (_callback: unknown): (() => void) => () => {},
  onTerminalOAuthCodeNeeded: (_callback: unknown): (() => void) => () => {},
  submitOAuthCode: async (_terminalId: string, _code: string): Promise<IPCResult> => {
    return { success: false, error: 'OAuth not supported in web version' };
  },
  onTerminalOnboardingComplete: (_callback: unknown): (() => void) => () => {},
  onTerminalPendingResume: (_callback: unknown): (() => void) => () => {},
  onTerminalProfileChanged: (_callback: unknown): (() => void) => () => {},

  // ===========================================================================
  // File Operations
  // ===========================================================================

  readDirectory: async (
    path: string
  ): Promise<IPCResult<{ files: FileEntry[]; path: string }>> => {
    const result = await filesApi.list(path, false);
    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }
    return {
      success: true,
      data: { files: result.data.entries, path: result.data.path },
    };
  },

  readFile: async (path: string): Promise<IPCResult<string>> => {
    const result = await filesApi.read(path);
    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data.content };
  },

  writeFile: async (path: string, content: string): Promise<IPCResult> => {
    const result = await filesApi.write(path, content);
    return { success: result.success, error: result.error };
  },

  deleteFile: async (path: string): Promise<IPCResult> => {
    const result = await filesApi.delete(path, true);
    return { success: result.success, error: result.error };
  },

  createDirectory: async (path: string): Promise<IPCResult> => {
    const result = await filesApi.mkdir(path);
    return { success: result.success, error: result.error };
  },

  renameFile: async (oldPath: string, newPath: string): Promise<IPCResult> => {
    const result = await filesApi.rename(oldPath, newPath);
    return { success: result.success, error: result.error };
  },

  getFileStat: async (path: string): Promise<IPCResult<unknown>> => {
    const result = await filesApi.stat(path);
    return { success: result.success, data: result.data, error: result.error };
  },

  // ===========================================================================
  // Settings Operations
  // ===========================================================================

  getSettings: async (): Promise<IPCResult<Settings>> => {
    const result = await settingsApi.getAll();
    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data.settings };
  },

  saveSettings: async (settings: Partial<Settings>): Promise<IPCResult<Settings>> => {
    const result = await settingsApi.update(settings);
    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data.settings };
  },

  getSetting: async <T>(key: string): Promise<IPCResult<T>> => {
    const result = await settingsApi.get(key);
    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data.value as T };
  },

  setSetting: async (key: string, value: unknown): Promise<IPCResult> => {
    const result = await settingsApi.set(key, value);
    return { success: result.success, error: result.error };
  },

  // ===========================================================================
  // Project Operations
  // ===========================================================================

  listProjects: async (): Promise<IPCResult<ProjectInfo[]>> => {
    const result = await projectsApi.list();
    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data.projects };
  },

  getProject: async (name: string): Promise<IPCResult<ProjectInfo>> => {
    const result = await projectsApi.get(name);
    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data };
  },

  createProject: async (options: {
    name: string;
    description?: string;
    tags?: string[];
  }): Promise<IPCResult<ProjectInfo>> => {
    const result = await projectsApi.create(options);
    if (!result.success || !result.data) {
      return { success: false, error: result.error };
    }
    return { success: true, data: result.data };
  },

  openProject: async (path: string): Promise<IPCResult> => {
    // In web version, "opening" a project means selecting it
    // The actual project path becomes the CWD for new terminals
    return { success: true, data: { path } };
  },

  // ===========================================================================
  // API Profile Operations (Simplified for Web)
  // ===========================================================================

  getAPIProfiles: async (): Promise<IPCResult<{ profiles: unknown[]; activeProfileId: string | null }>> => {
    // Web version doesn't use API profiles - auth is handled server-side
    return {
      success: true,
      data: {
        profiles: [],
        activeProfileId: null,
      },
    };
  },

  saveAPIProfile: async (_profile: unknown): Promise<IPCResult<unknown>> => {
    return { success: false, error: 'API profile management not supported in web version' };
  },

  updateAPIProfile: async (_profile: unknown): Promise<IPCResult<unknown>> => {
    return { success: false, error: 'API profile management not supported in web version' };
  },

  deleteAPIProfile: async (_profileId: string): Promise<IPCResult> => {
    return { success: false, error: 'API profile management not supported in web version' };
  },

  setActiveAPIProfile: async (_profileId: string | null): Promise<IPCResult> => {
    return { success: false, error: 'API profile management not supported in web version' };
  },

  testConnection: async (_baseUrl: string, _apiKey: string, _signal?: AbortSignal): Promise<IPCResult<unknown>> => {
    return { success: false, error: 'Connection testing not supported in web version' };
  },

  discoverModels: async (_baseUrl: string, _apiKey: string, _signal?: AbortSignal): Promise<IPCResult<unknown>> => {
    return { success: false, error: 'Model discovery not supported in web version' };
  },

  // ===========================================================================
  // Claude Profile Operations (Simplified for Web)
  // ===========================================================================

  getClaudeProfiles: async (): Promise<IPCResult<unknown>> => {
    // Web version uses server's OAuth, not local profiles
    return {
      success: true,
      data: {
        profiles: [],
        activeProfileId: null,
        autoSwitch: { enabled: false },
      },
    };
  },

  saveClaudeProfile: async (_profile: unknown): Promise<IPCResult<unknown>> => {
    return { success: false, error: 'Profile management not supported in web version' };
  },

  deleteClaudeProfile: async (_profileId: string): Promise<IPCResult> => {
    return { success: false, error: 'Profile management not supported in web version' };
  },

  setActiveClaudeProfile: async (_profileId: string): Promise<IPCResult> => {
    return { success: false, error: 'Profile management not supported in web version' };
  },

  switchClaudeProfile: async (_terminalId: string, _profileId: string): Promise<IPCResult> => {
    return { success: false, error: 'Profile switching not supported in web version' };
  },

  // ===========================================================================
  // App Update (Not applicable for web)
  // ===========================================================================

  checkForUpdates: async (): Promise<IPCResult<unknown>> => {
    return { success: true, data: { updateAvailable: false } };
  },

  downloadUpdate: async (): Promise<IPCResult> => {
    return { success: false, error: 'Updates not supported in web version' };
  },

  installUpdate: async (): Promise<IPCResult> => {
    return { success: false, error: 'Updates not supported in web version' };
  },

  onUpdateAvailable: (_callback: unknown): (() => void) => () => {},
  onUpdateDownloaded: (_callback: unknown): (() => void) => () => {},
  onUpdateError: (_callback: unknown): (() => void) => () => {},
  onUpdateProgress: (_callback: unknown): (() => void) => () => {},

  // ===========================================================================
  // Event Listeners (No-op stubs for web compatibility)
  // ===========================================================================

  onProactiveSwapNotification: (_callback: unknown): (() => void) => () => {},
  onAppUpdateAvailable: (_callback: unknown): (() => void) => () => {},
  onTaskProgress: (_callback: unknown): (() => void) => () => {},
  onTaskComplete: (_callback: unknown): (() => void) => () => {},
  onTaskError: (_callback: unknown): (() => void) => () => {},
  onWindowFocus: (_callback: unknown): (() => void) => () => {},
  onWindowBlur: (_callback: unknown): (() => void) => () => {},

  // ===========================================================================
  // Debug/Development
  // ===========================================================================

  openDevTools: (): void => {
    // In web, developer can just press F12
    console.log('Use F12 or browser dev tools');
  },

  getAppVersion: async (): Promise<IPCResult<string>> => {
    return { success: true, data: '1.0.0-web' };
  },

  // ===========================================================================
  // Platform Info
  // ===========================================================================

  getPlatform: (): string => {
    return 'web';
  },

  isElectron: (): boolean => {
    return false;
  },

  // ===========================================================================
  // Health Check
  // ===========================================================================

  checkHealth: async (): Promise<IPCResult<{ status: string }>> => {
    const result = await healthApi.check();
    return { success: result.success, data: result.data, error: result.error };
  },
};

// =============================================================================
// Expose as window.electronAPI for compatibility
// =============================================================================

/**
 * Create a no-op stub that returns appropriate default values
 * based on method name patterns
 */
function createStub(methodName: string): (...args: unknown[]) => unknown {
  // Event listeners (on*) return unsubscribe functions
  if (methodName.startsWith('on')) {
    return (_callback: unknown) => () => {};
  }
  
  // Methods that return lists
  if (methodName.startsWith('list') || methodName.startsWith('get') && methodName.endsWith('s')) {
    return async () => ({ success: true, data: [] });
  }
  
  // Methods that check/verify something
  if (methodName.startsWith('check') || methodName.startsWith('is') || methodName.startsWith('has')) {
    return async () => ({ success: true, data: false });
  }
  
  // Methods that get a current/active item
  if (methodName.includes('Current') || methodName.includes('Active')) {
    return async () => ({ success: true, data: null });
  }
  
  // Methods that get status
  if (methodName.includes('Status') || methodName.includes('State')) {
    return async () => ({ success: true, data: { status: 'unavailable' } });
  }
  
  // Methods that get config/settings
  if (methodName.includes('Config') || methodName.includes('Settings') || methodName.includes('Profile')) {
    return async () => ({ success: true, data: {} });
  }
  
  // Default: return success with null data
  return async () => ({ success: true, data: null });
}

/**
 * Create a recursive Proxy that auto-stubs any missing property/method
 * Handles nested objects like electronAPI.provider.getCurrent
 */
function createAutoStubProxy(target: Record<string, unknown> = {}, path = 'electronAPI'): unknown {
  return new Proxy(target, {
    get(obj, prop: string) {
      // Return existing property if it exists
      if (prop in obj) {
        return obj[prop];
      }
      
      // Handle special properties
      if (prop === 'then' || prop === 'toJSON' || typeof prop === 'symbol') {
        return undefined;
      }
      
      const fullPath = `${path}.${prop}`;
      console.debug(`[WebAPI] Auto-stubbing: ${fullPath}`);
      
      // Create a stub that works as both a function AND an object with methods
      const stub = createStub(prop);
      
      // Make the stub also act as a nested object with auto-stubbed methods
      return createAutoStubProxy(
        Object.assign(stub, {}),
        fullPath
      );
    },
  });
}

/**
 * Initialize web API and expose as window.electronAPI
 * Uses recursive Proxy to auto-stub any missing Electron API methods,
 * including nested objects like provider.getCurrent, github.onPRReviewProgress
 */
export function initializeWebAPI(): void {
  if (typeof window !== 'undefined') {
    // Wrap webAPI in recursive Proxy to handle all missing methods/properties
    (window as any).electronAPI = createAutoStubProxy(webAPI as Record<string, unknown>);
    (window as any).__IS_WEB_VERSION__ = true;
    console.log('Web API initialized - window.electronAPI is now available');
  }
}

// Export types
export type WebAPI = typeof webAPI;
